import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Textarea } from "@/shared/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  ChevronsUpDown,
  FileText,
  Hash,
  Image as ImageIcon,
  ImagePlus,
  List,
  ListFilter,
  Loader2,
  ToggleLeft,
  Type,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { uploadSurveyPhoto } from "@/shared/lib/firebaseStorage";
import { resizeImage } from "@/shared/lib/imageProcessing";

export function SelectFieldEditor({
  title,
  value,
  options,
  refId,
  onUpdate,
}: {
  title: string;
  value: string | null;
  options: string[];
  refId: string;
  onUpdate: (refId: string, value: string | null) => void;
}) {
  // Ensure current value is in options; if not, add it as first option
  const allOptions =
    value && !options.includes(value) ? [value, ...options] : options;

  return (
    <div className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-accent/20 transition-colors">
      <Label className="text-sm text-muted-foreground shrink-0 min-w-[140px] max-w-[50%] flex items-center gap-1.5">
        <ListFilter className="h-3.5 w-3.5 text-blue-400/70 shrink-0" />
        {title}
      </Label>
      <Select
        value={value ?? "__empty__"}
        onValueChange={(v) => onUpdate(refId, v === "__empty__" ? null : v)}
      >
        <SelectTrigger className="max-w-[360px] h-8 text-sm">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__empty__">
            <span className="text-muted-foreground italic">— None —</span>
          </SelectItem>
          {allOptions.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TextFieldEditor({
  title,
  value,
  refId,
  onUpdate,
}: {
  title: string;
  value: string | null;
  refId: string;
  onUpdate: (refId: string, value: string | null) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-accent/20 transition-colors">
      <Label className="text-sm text-muted-foreground shrink-0 min-w-[140px] flex items-center gap-1.5">
        <Type className="h-3.5 w-3.5 text-emerald-400/70 shrink-0" />
        {title}
      </Label>
      <Input
        className="max-w-[300px] h-8 text-sm"
        value={value ?? ""}
        onChange={(e) => onUpdate(refId, e.target.value || null)}
        placeholder="—"
      />
    </div>
  );
}

export function NumberFieldEditor({
  title,
  value,
  refId,
  onUpdate,
}: {
  title: string;
  value: number | null;
  refId: string;
  onUpdate: (refId: string, value: number | null) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-accent/20 transition-colors">
      <Label className="text-sm text-muted-foreground shrink-0 min-w-[140px] flex items-center gap-1.5">
        <Hash className="h-3.5 w-3.5 text-amber-400/70 shrink-0" />
        {title}
      </Label>
      <Input
        type="number"
        className="max-w-[160px] h-8 text-sm"
        value={value ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          onUpdate(refId, v === "" ? null : parseFloat(v));
        }}
        placeholder="—"
      />
    </div>
  );
}

export function BooleanFieldEditor({
  title,
  value,
  refId,
  onUpdate,
}: {
  title: string;
  value: boolean;
  refId: string;
  onUpdate: (refId: string, value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-accent/20 transition-colors">
      <Label className="text-sm text-muted-foreground flex items-center gap-1.5">
        <ToggleLeft className="h-3.5 w-3.5 text-purple-400/70 shrink-0" />
        {title}
      </Label>
      <div className="flex items-center gap-2">
        <Switch
          checked={value}
          onCheckedChange={(checked) => onUpdate(refId, checked)}
        />
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            value
              ? "border-green-500/50 text-green-400"
              : "border-red-500/50 text-red-400"
          )}
        >
          {value ? "Yes" : "No"}
        </Badge>
      </div>
    </div>
  );
}

export function AreaFieldEditor({
  title,
  value,
  refId,
  onUpdate,
}: {
  title: string;
  value: string | null;
  refId: string;
  onUpdate: (refId: string, value: string | null) => void;
}) {
  return (
    <div className="py-2 px-3">
      <div className="flex items-center gap-1.5 mb-2">
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        <Label className="text-sm text-muted-foreground">{title}</Label>
      </div>
      <Textarea
        className="text-sm min-h-[80px]"
        value={value ?? ""}
        onChange={(e) => onUpdate(refId, e.target.value || null)}
        placeholder="Enter text..."
      />
    </div>
  );
}

export function MultiSelectFieldEditor({
  title,
  values,
  options,
  refId,
  onUpdate,
}: {
  title: string;
  values: string[] | null;
  options: string[];
  refId: string;
  onUpdate: (refId: string, value: string[] | null) => void;
}) {
  const safeValues = values || [];
  // Ensure current values are in options
  const allOptions = Array.from(new Set([...options, ...safeValues]));

  const toggleOption = (opt: string) => {
    const next = safeValues.includes(opt)
      ? safeValues.filter((v) => v !== opt)
      : [...safeValues, opt];
    onUpdate(refId, next.length > 0 ? next : null);
  };

  return (
    <div className="flex items-start justify-between gap-4 py-2 px-3 rounded-lg hover:bg-accent/20 transition-colors">
      <div className="flex items-center gap-1.5 shrink-0 min-w-[140px] pt-1">
        <List className="h-3.5 w-3.5 text-cyan-400/70 shrink-0" />
        <Label className="text-sm text-muted-foreground">{title}</Label>
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-sm min-w-[200px] max-w-[360px] justify-between font-normal"
          >
            <span className="truncate">
              {safeValues.length === 0
                ? "Select options..."
                : safeValues.join(", ")}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 ml-2 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="end">
          <div className="max-h-[240px] overflow-y-auto p-2 space-y-1">
            {allOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No options available</p>
            ) : (
              allOptions.map((opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={safeValues.includes(opt)}
                    onCheckedChange={() => toggleOption(opt)}
                  />
                  {opt}
                </label>
              ))
            )}
          </div>
          {safeValues.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground"
                onClick={() => onUpdate(refId, null)}
              >
                Clear all
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function PhotosFieldView({
  title,
  urls,
  propertyId,
  surveyId,
  nodePath,
  refId,
  onUpdate,
  onOpenPhotos,
}: {
  title: string;
  urls: string[] | null;
  propertyId: string;
  surveyId: string | null;
  nodePath: string[];
  refId: string;
  onUpdate: (refId: string, value: string[] | null) => void;
  onOpenPhotos: (urls: string[], index: number, title: string) => void;
}) {
  const safeUrls = urls ?? [];
  // In-flight uploads: id → progress 0..1, each rendered as a placeholder tile.
  const [uploads, setUploads] = useState<Record<string, number>>({});
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Always-fresh ref into the latest urls so concurrent uploads append
  // instead of clobbering each other.
  const urlsRef = useRef<string[]>(safeUrls);
  urlsRef.current = safeUrls;

  const uploadFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        toast.error("Only images can be added to photo questions");
        return;
      }
      if (!surveyId) {
        toast.error("Survey is not loaded yet — try again in a moment");
        return;
      }
      const uploadId = crypto.randomUUID();
      setUploads((u) => ({ ...u, [uploadId]: 0 }));
      try {
        const resized = await resizeImage(file);
        const { downloadUrl } = await uploadSurveyPhoto(
          { propertyId, surveyId, nodePath },
          resized,
          {
            onProgress: (percent) =>
              setUploads((u) => (uploadId in u ? { ...u, [uploadId]: percent } : u)),
          }
        );
        onUpdate(refId, [...urlsRef.current, downloadUrl]);
      } catch (err) {
        console.error("Photo upload failed:", err);
        toast.error(err instanceof Error ? err.message : "Photo upload failed");
      } finally {
        setUploads((u) => {
          const { [uploadId]: _done, ...rest } = u;
          return rest;
        });
      }
    },
    [propertyId, surveyId, nodePath, refId, onUpdate]
  );

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((f) => void uploadFile(f));
    e.target.value = "";
  };

  const removeAt = (idx: number) => {
    const next = safeUrls.slice();
    next.splice(idx, 1);
    onUpdate(refId, next.length > 0 ? next : null);
    // Note: we do not delete the underlying object from Firebase Storage here.
    // Orphan cleanup is out of scope, matching EvidenceUploader behavior.
  };

  const addButton = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-14 w-14 p-0"
        onClick={() => fileInputRef.current?.click()}
        aria-label={`Add photo to ${title}`}
      >
        <ImagePlus className="h-4 w-4" />
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={onPick}
      />
    </>
  );

  return (
    <div className="py-2 px-3">
      <div className="flex items-center gap-1.5 mb-2">
        <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{title}</span>
        {safeUrls.length > 0 && (
          <Badge variant="outline" className="text-xs ml-auto">
            {safeUrls.length}
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {safeUrls.map((url, idx) => (
          <div
            key={url + idx}
            className="group relative h-14 w-14 rounded-lg overflow-hidden border border-border/50 hover:border-primary/50 transition-all"
          >
            <button
              type="button"
              onClick={() => onOpenPhotos(safeUrls, idx, title)}
              className="h-full w-full block cursor-zoom-in"
              aria-label={`Open ${title} photo ${idx + 1}`}
            >
              <img
                src={url}
                alt={`${title} ${idx + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDeleteIdx(idx);
              }}
              className="absolute top-0 right-0 bg-background/80 rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition"
              aria-label={`Remove ${title} photo ${idx + 1}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {Object.entries(uploads).map(([id, percent]) => (
          <div
            key={id}
            className="relative flex h-14 w-14 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-lg border border-border/50 bg-muted/30"
            aria-label="Uploading photo"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {Math.round(percent * 100)}%
            </span>
            <div
              className="absolute bottom-0 left-0 h-0.5 bg-primary transition-[width] duration-200"
              style={{ width: `${Math.round(percent * 100)}%` }}
            />
          </div>
        ))}
        {addButton}
      </div>

      <AlertDialog
        open={pendingDeleteIdx !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteIdx(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the photo from "{title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDeleteIdx !== null && safeUrls[pendingDeleteIdx] && (
            <div className="flex justify-center py-2">
              <img
                src={safeUrls[pendingDeleteIdx]}
                alt="Photo to remove"
                className="h-24 w-24 object-cover rounded border border-border/50"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDeleteIdx !== null) {
                  removeAt(pendingDeleteIdx);
                  setPendingDeleteIdx(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
